import { Box, Grid } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DistributionPanel } from "../components/AnalyticsVisuals";

import {
  AppLoader,
  AppSnackbar,
} from "../../../../components/common";

import AnalyticsHeader from "../components/AnalyticsHeader";
import Filters from "../components/Filters";
import SummaryCards from "../components/SummaryCards";
import InventoryAnalytics from "../components/InventoryAnalytics";
import VehicleAnalytics from "../components/VehicleAnalytics";
import MaintenanceAnalytics from "../components/MaintenanceAnalytics";
import PurchaseAnalytics from "../components/PurchaseAnalytics";
import SupplierAnalytics from "../components/SupplierAnalytics";
import MonthlyTrendChart from "../components/MonthlyTrendChart";
import StockMovementChart from "../components/StockMovementChart";
import TopMovingPartsTable from "../components/TopMovingPartsTable";
import LowStockAnalytics from "../components/LowStockAnalytics";
import { useAnalytics } from "../hooks/useAnalytics";

const AnalyticsPage = () => {
  const {
    dashboard,
    inventory,
    maintenance,
    purchase,
    supplier,
    topMovingParts,
    lowStockItems,
    filterOptions,
    filters,
    loading,
    error,
    refresh,
    updateFilters,
    exportReport,
  } = useAnalytics();

  if (loading && !dashboard) {
    return <AppLoader />;
  }

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 1.5, md: 2.5 },
        minHeight: "calc(100vh - 96px)",
        borderRadius: 3,
        background: theme.palette.mode === "dark" ? "linear-gradient(135deg, #111e33, #0e1727)" : "linear-gradient(135deg, #edf5ff, #f5faff 50%, #eaf6ff)",
        "& .MuiCard-root, & .MuiPaper-root": {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
          backdropFilter: "none",
          borderRadius: "14px",
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, .10),
          boxShadow: "0 4px 18px rgba(35,75,130,.035)",
          "&:hover": { boxShadow: "0 4px 18px rgba(35,75,130,.035)" },
        },
        "& .MuiTableCell-root": { fontSize: ".73rem", borderColor: alpha(theme.palette.primary.main, .09) },
        "& .MuiTableCell-head": { fontWeight: 700, bgcolor: "action.hover", color: "text.secondary" },
        "& .MuiButton-root": { borderRadius: "8px", fontSize: ".75rem" },
      })}
    >
      <AnalyticsHeader
        loading={loading}
        onRefresh={refresh}
        onExport={exportReport}
      />

      <Box mt={2}>
        <Filters
          filters={filters}
          hubs={filterOptions.hubs}
          categories={filterOptions.categories}
          suppliers={filterOptions.suppliers}
          onChange={updateFilters}
          onReset={() =>
            updateFilters({
              fromDate: "",
              toDate: "",
              startDate: "",
              endDate: "",
              warehouseId: "",
              hubId: "",
              categoryId: "",
              supplierId: "",
            })
          }
        />
      </Box>

      <Box mt={2}>
        <SummaryCards dashboard={dashboard} />

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6} xl={5}><MonthlyTrendChart dashboard={dashboard} /></Grid>
          <Grid item xs={12} md={6} xl={4}><StockMovementChart dashboard={dashboard} /></Grid>
          <Grid item xs={12} md={6} xl={3}><DistributionPanel inventory={inventory} /></Grid>
          <Grid item xs={12} md={6} xl={4}>
            <InventoryAnalytics data={inventory} />
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <MaintenanceAnalytics data={maintenance} />
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <PurchaseAnalytics data={purchase} />
          </Grid>

          <Grid item xs={12} md={6} xl={4}>
            <SupplierAnalytics data={supplier} />
          </Grid>
          <Grid item xs={12} md={6} xl={4}><DistributionPanel inventory={inventory} categories /></Grid>
          <Grid item xs={12} md={6} xl={4}><VehicleAnalytics data={dashboard?.vehicleAnalytics} /></Grid>
        </Grid>
      </Box>

      <Box mt={1.5}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={7}>
            <TopMovingPartsTable rows={topMovingParts} loading={loading} />
          </Grid>

          <Grid item xs={12} lg={5}>
            <LowStockAnalytics items={lowStockItems} loading={loading} />
          </Grid>
        </Grid>
      </Box>

      <AppSnackbar
        open={!!error}
        severity="error"
        message={error ?? ""}
        onClose={() => {}}
      />
    </Box>
  );
};

export default AnalyticsPage;
